/* ============================================================
   screen-config-modal.js
   Zentrale neue Modal-Logik für neue Fahrzeuge
============================================================ */

import { doAjax } from './helper'

const STORAGE_KEY = 'OT_CONFIG_MODAL_STATE'

const defaultState = {
  components: {}, // z.B. { rs5_steering_wheel: '74' }
  packages: {}    // z.B. { q7q9_yn1_choice: '7HH_Q7Q9' }
}



function loadState() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY)
    if (!raw) return { ...defaultState, components: {}, packages: {} }

    const parsed = JSON.parse(raw)
    return {
      components: parsed.components || {},
      packages: parsed.packages || {}
    }
  } catch (e) {
    console.warn('[CONFIG_MODAL] Konnte State nicht laden', e)
    return { ...defaultState, components: {}, packages: {} }
  }
}

function saveState() {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(modalState))
  } catch (e) {
    console.warn('[CONFIG_MODAL] Konnte State nicht speichern', e)
  }
}

const modalState = loadState()

function getConfigModalTranslation(key, fallback = '') {
  // Support both translation objects:
  // - window.configModalTranslations (newer)
  // - window.modalTranslations (legacy)
  // Use nullish coalescing to allow empty-string translations.
  return window.configModalTranslations?.[key] ?? window.modalTranslations?.[key] ?? fallback
}

function getOptionModalLabel(option = {}) {
  const code = option.code || ''
  const fallback = option.label || code || ''

  if (option.modalLabel !== undefined && option.modalLabel !== null && option.modalLabel !== '') {
    return option.modalLabel
  }

  if (option.modalLabelKey) {
    return getConfigModalTranslation(
      option.modalLabelKey,
      fallback
    )
  }

  if (code) {
    const modalLabelFromTranslation = getConfigModalTranslation(
      `modallabel_${code}`,
      ''
    )

    if (modalLabelFromTranslation) {
      return modalLabelFromTranslation
    }
  }

  return fallback
}

function ensureModalRoot() {
  let modal = document.querySelector('.modal:not(#flip-modal):not(#reset-modal)')

  if (modal) return modal

  modal = document.createElement('div')
  modal.className = 'modal hidden'
  modal.innerHTML = `
    <div class="modal-inner">
      <div class="close">
        <img src="img/icons/static/cancel-small.svg" alt="close icon">
      </div>

      <h6 class="modal-title"></h6>

      <div class="modal-checkboxes"></div>

      <div class="modal-buttons">
        <button class="cancel aui-button aui-button--secondary aui-js-response is-upgraded" type="button">
          ${getConfigModalTranslation('cancel', 'Abbrechen')}
          <span class="aui-response aui-response--masked">
            <span class="aui-response__effect aui-response__effect--large"></span>
          </span>
        </button>

        <button class="submit aui-button aui-button--primary aui-js-response is-upgraded" type="button">
          ${getConfigModalTranslation('submit', 'Konfiguration übernehmen')}
          <span class="aui-response aui-response--masked">
            <span class="aui-response__effect aui-response__effect--large"></span>
          </span>
        </button>
      </div>
    </div>
  `

  document.body.appendChild(modal)
  return modal
}

function refreshGroupState(wrapper, groupName, type) {
  if (!wrapper) return

  const allLabels = wrapper.querySelectorAll('label[data-auicode]')
  const allInputs = wrapper.querySelectorAll('.aui-checkbox__input')
  const checkedInput = wrapper.querySelector('.aui-checkbox__input:checked')

  // Radio-Button-Verhalten im Modal:
  // Alle Optionen bleiben klickbar, aber pro Gruppe darf nur eine aktiv sein.
  allLabels.forEach((lbl) => lbl.classList.remove('is-checked'))
  allInputs.forEach((inp) => {
    inp.disabled = false
  })

  if (!checkedInput) {
    delete wrapper.dataset.selectedCode
    delete wrapper.dataset.groupName
    return
  }

  const activeLabel = checkedInput.closest('label[data-auicode]')
  if (!activeLabel) return

  activeLabel.classList.add('is-checked')
  wrapper.dataset.selectedCode = activeLabel.dataset.auicode
  wrapper.dataset.groupName = groupName
}

function renderOption(option, groupName, type = 'package', checked = false) {
  const label = document.createElement('label')
  label.className = 'aui-checkbox aui-js-checkbox is-upgraded'
  label.dataset.auicode = option.code

  if (checked) {
    label.classList.add('is-checked')
  }

  const optionLabel = getOptionModalLabel(option)

  label.innerHTML = `
    <input class="aui-checkbox__input" type="checkbox" ${checked ? 'checked' : ''}>
    <span class="aui-checkbox__label"></span>
    <span class="aui-checkbox__box"><span class="aui-checkbox__tick"></span></span>
  `

  const labelText = label.querySelector('.aui-checkbox__label')
  if (labelText) {
    labelText.textContent = optionLabel
  }

  const input = label.querySelector('input')

  input.addEventListener('change', () => {
    const wrapper = label.closest('[data-modal-group]')
    if (!wrapper) return

    const allInputs = wrapper.querySelectorAll('.aui-checkbox__input')

    // Radio-Button-Verhalten:
    // Eine aktive Auswahl kann nicht leer geklickt werden.
    // Stattdessen kann direkt eine andere Option der Gruppe gewählt werden.
    if (!input.checked) {
      const hasOtherChecked = Array.from(allInputs).some((otherInput) => otherInput.checked)

      if (!hasOtherChecked) {
        input.checked = true
      }

      refreshGroupState(wrapper, groupName, type)
      return
    }

    allInputs.forEach((otherInput) => {
      if (otherInput !== input) {
        otherInput.checked = false
      }
    })

    refreshGroupState(wrapper, groupName, type)
  })

  setTimeout(() => {
    const wrapper = label.closest('[data-modal-group]')
    if (!wrapper) return
    refreshGroupState(wrapper, groupName, type)
  }, 0)

  return label
}

function triggerPackageClick(packageCode) {
  const label = document.querySelector(`label[data-auicode="${packageCode}"]`)
  if (!label) {
    console.warn('[CONFIG_MODAL] Paket im DOM nicht gefunden:', packageCode)
    return false
  }

  const input = label.querySelector('input')
  if (!input) {
    console.warn('[CONFIG_MODAL] Input für Paket nicht gefunden:', packageCode)
    return false
  }

  if (input.checked) return true

  input.click()
  return true
}

function buildGroups(container, groups = []) {
  container.innerHTML = ''

  groups.forEach((group, index) => {
    const groupWrap = document.createElement('div')
    groupWrap.dataset.modalGroup = group.id
    groupWrap.dataset.groupType = group.type
    groupWrap.dataset.groupName = group.id

    if (group.title) {
      const title = document.createElement('h6')
      title.className = 'modal-group-title'
      title.textContent = group.title
      groupWrap.appendChild(title)
    }

    const rememberedCode =
      group.type === 'component'
        ? modalState.components[group.id]
        : modalState.packages[group.id]

    const availableCodes = group.options.map((option) => String(option.code))
    const rememberedIsValid =
      rememberedCode !== undefined &&
      rememberedCode !== null &&
      rememberedCode !== '' &&
      availableCodes.includes(String(rememberedCode))

    group.options.forEach((option) => {
      // Nur gültige rememberedCodes nutzen – sonst option.default
      // (z. B. alter Code '0', wenn die Option für Q9 entfällt)
      const checked = rememberedIsValid
        ? String(rememberedCode) === String(option.code)
        : option.default === true

      const optionNode = renderOption(option, group.id, group.type, checked)

      if (checked) {
        groupWrap.dataset.selectedCode = option.code
      }

      groupWrap.appendChild(optionNode)
    })

    container.appendChild(groupWrap)

    // State nach initialem Render sauber setzen
    setTimeout(() => {
      refreshGroupState(groupWrap, group.id, group.type)
    }, 0)

    if (index < groups.length - 1) {
      const line = document.createElement('div')
      line.className = 'horizontal-line'
      container.appendChild(line)
    }
  })
}

function collectSelections(modal) {
  const result = []

  modal.querySelectorAll('[data-modal-group]').forEach((groupEl) => {
    const groupId = groupEl.dataset.groupName
    const type = groupEl.dataset.groupType
    const selectedCode = groupEl.dataset.selectedCode || ''

    result.push({
      id: groupId,
      type,
      code: selectedCode
    })
  })

  return result
}

function validateSelections(selections = []) {
  return selections.every((entry) => entry.code && entry.code.trim() !== '')
}

function applySelections(selections = []) {
  const packageSelections = selections.filter((entry) => entry.type === 'package')
  const componentSelections = selections.filter((entry) => entry.type === 'component')

  packageSelections.forEach((entry) => {
    modalState.packages[entry.id] = entry.code
    triggerPackageClick(entry.code)
  })

  componentSelections.forEach((entry) => {
    modalState.components[entry.id] = entry.code

    saveComponentStateWithAjax(entry.id, entry.code, (response) => {
      // optional debug
      console.log('[CONFIG_MODAL] Komponente gespeichert', entry.id, entry.code, response)
    })
  })

  saveState()
}

function wireModalEvents(modal, handlers) {
  const close = modal.querySelector('.close')
  const cancel = modal.querySelector('.cancel')
  const submit = modal.querySelector('.submit')

  const closeModal = () => {
    modal.classList.add('hidden')
  }

  close.onclick = () => {
    closeModal()
    handlers.onCancel?.()
  }

  cancel.onclick = () => {
    closeModal()
    handlers.onCancel?.()
  }

  modal.onclick = (e) => {
    if (e.target === modal) {
      closeModal()
      handlers.onCancel?.()
    }
  }

  submit.onclick = () => {
    const selections = collectSelections(modal)

    if (!validateSelections(selections)) {
      console.warn(
        '[CONFIG_MODAL]',
        getConfigModalTranslation(
            'validationMissingSelection',
            'Bitte alle erforderlichen Entscheidungen treffen'
          )
      )
      return
    }

    applySelections(selections)
    closeModal()
    handlers.onSubmit?.(selections, getConfigModalState())
  }
}


function saveComponentStateWithAjax(componentKey, componentValue, callback = () => {}) {
  if (!componentKey) {
    console.warn('[CONFIG_MODAL] componentKey fehlt')
    callback(false)
    return
  }

  if (componentValue === undefined || componentValue === null || componentValue === '') {
    console.warn('[CONFIG_MODAL] componentValue fehlt für', componentKey)
    callback(false)
    return
  }

  const payload = {}

  if (componentKey === 'q7q9_steering_wheel') {
    payload.choose_steering_wheel = componentValue
  } else if (componentKey === 'q7_seven_seater') {
    payload.choose_seven_seater = componentValue
  } else {
    payload.component_selection_key = componentKey
    payload.component_selection_value = componentValue
  }

  doAjax(payload, (response) => {
    if (response?.maxpages) {
      const max = document.querySelector('.aui-pager__max')
      if (max) max.innerHTML = response.maxpages
    }

    callback(response)
  })
}

export function openConfigModal({
  title = getConfigModalTranslation(
    'followupTitle',
    'Diese Auswahl erfordert weitere Anpassungen Ihrer Konfiguration'
  ),
  groups = [],
  onCancel = () => {},
  onSubmit = () => {}
} = {}) {
  const modal = ensureModalRoot()
  const titleEl = modal.querySelector('.modal-title')
  const checkboxesEl = modal.querySelector('.modal-checkboxes')
  const cancelBtn = modal.querySelector('.cancel')
  const submitBtn = modal.querySelector('.submit')

  titleEl.textContent = title

  if (cancelBtn) {
    cancelBtn.childNodes[0].nodeValue = `${getConfigModalTranslation('cancel', 'Abbrechen')} `
  }

  if (submitBtn) {
    submitBtn.childNodes[0].nodeValue = `${getConfigModalTranslation('submit', 'Konfiguration übernehmen')} `
  }

  buildGroups(checkboxesEl, groups)
  wireModalEvents(modal, { onCancel, onSubmit })

  modal.classList.remove('hidden')
}


export function getConfigModalState() {
  return {
    components: { ...modalState.components },
    packages: { ...modalState.packages }
  }
}

export function getStoredComponentChoice(groupId) {
  return modalState.components[groupId] || null
}

export function getStoredPackageChoice(groupId) {
  return modalState.packages[groupId] || null
}

export function setStoredComponentChoice(groupId, code) {
  modalState.components[groupId] = code
  saveState()
}

export function setStoredPackageChoice(groupId, code) {
  modalState.packages[groupId] = code
  saveState()
}

export function clearStoredChoice(groupId) {
  delete modalState.components[groupId]
  delete modalState.packages[groupId]
  saveState()
}
